import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  Stack,
  Typography,
  IconButton,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { Close, UploadFile, ContentCopy } from "@mui/icons-material";

import MDButton from "components/MDButton";
import useMediaUpload from "hooks/useMediaUpload";
import { listMediaAssets } from "api/endpoints/media";

// Upload / pick images for an email template. Uploads run through the F2
// 3-step flow (useMediaUpload); existing website assets are listed for reuse.
// Picking an asset inserts a hosted <img> tag back into the raw HTML editor.
const EmailImageDialog = ({ open, onClose, websiteId, onInsert }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { upload, progress, status } = useMediaUpload({ websiteId });
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    listMediaAssets(websiteId, { limit: 50 })
      .then((res) => {
        if (!cancelled) setAssets(res.items || []);
      })
      .catch((err) => {
        if (!cancelled) {
          enqueueSnackbar(err.normalizedMessage || "Failed to load images", { variant: "error" });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, websiteId, enqueueSnackbar]);

  const handleUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    const altText = window.prompt("Image alt text (optional):", "") || "";
    try {
      const asset = await upload(file, { altText, folderPath: "email-templates" });
      setAssets((prev) => [asset, ...prev]);
      enqueueSnackbar("Image uploaded", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.normalizedMessage || "Upload failed", { variant: "error" });
    }
  };

  const handleInsert = (asset) => {
    const alt = asset.alt_text || "";
    onInsert(`<img src="${asset.file_url}" alt="${alt}" style="max-width:100%;" />`);
    onClose();
  };

  const handleCopy = async (asset) => {
    try {
      await navigator.clipboard.writeText(asset.file_url);
      enqueueSnackbar("Image URL copied", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy the URL", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Images
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <MDButton variant="gradient" color="info" component="label" startIcon={<UploadFile />}>
              Upload Image
              <input type="file" accept="image/*" hidden onChange={handleUpload} />
            </MDButton>
          </Box>

          {status === "uploading" && <LinearProgress variant="determinate" value={progress} />}

          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {!loading && assets.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No images yet. Upload one to get a hosted URL you can use in the template.
            </Typography>
          )}

          {!loading && assets.length > 0 && (
            <Grid container spacing={2}>
              {assets.map((asset) => (
                <Grid item xs={6} sm={4} key={asset.media_asset_id}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={asset.file_url}
                      alt={asset.alt_text || ""}
                      sx={{
                        width: "100%",
                        height: 110,
                        objectFit: "contain",
                        display: "block",
                        mb: 1,
                        bgcolor: "grey.100",
                      }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MDButton
                        size="small"
                        variant="gradient"
                        color="info"
                        fullWidth
                        onClick={() => handleInsert(asset)}
                      >
                        Insert
                      </MDButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(asset)}
                        aria-label="Copy image URL"
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

EmailImageDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  websiteId: PropTypes.string,
  onInsert: PropTypes.func.isRequired,
};

EmailImageDialog.defaultProps = {
  websiteId: undefined,
};

export default EmailImageDialog;
